// src/app/modules/Members/pages/Spaces.jsx
import React, { useEffect, useState, useRef } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "../../../Dashbord/_redux/dashboard/Actions";
import { useOrgConfig } from "../../../../../hooks/useOrgConfig";
import { MembersTable } from "../database";
import { InviteMembersModal } from "../components/InviteMembersModal";
import { EditSpaceModal } from "../components/EditSpaceModal";
import { ConfirmActionModal } from '../../../../components/ConfirmActionModal';
import './spaces.scss';

import { LayoutGrid, Plus, Search, Filter } from 'lucide-react';

export function Spaces() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const { getLabel, org } = useOrgConfig();

  const { members, membersMeta, loading } = useSelector(
    (state) => ({
      members:     state.dashboard.space,
      membersMeta: state.dashboard.spaceMeta,
      loading:     state.dashboard.listLoading,
    }),
    shallowEqual
  );

  const [currentPage,      setCurrentPage]      = useState(1);
  const [perPage]                               = useState(10);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [showCreateModal,  setShowCreateModal]  = useState(false);
  const [showEditModal,    setShowEditModal]    = useState(false);
  const [selectedSpace,    setSelectedSpace]    = useState(null);
  const [showDeleteModal,  setShowDeleteModal]  = useState(false);
  const [actionLoading,    setActionLoading]    = useState(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => { init_functions(); }, []);
  useEffect(() => { if (org?.organization_id) fetchSpaces(); }, [currentPage]);

  function init_functions() {
    if (org?.organization_id && isMountedRef.current) fetchSpaces();
  }

  function fetchSpaces() {
    if (!isMountedRef.current) return;
    const params = { page: currentPage, limit: perPage, organization_id: org.organization_id };
    if (searchTerm) params.search = searchTerm;
    dispatch(actions.fetchAllData('spaces', org.organization_id, params));
  }

  function handleSearch()        { setCurrentPage(1); fetchSpaces(); }
  function handleViewDetails(id) { navigate(`/spaces/view/${id}`); }

  // ── Edit ──────────────────────────────────────────────────────────────────
  function handleEdit(space) {
    setSelectedSpace(space);
    setShowEditModal(true);
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  function handleDelete(id) {
    setSelectedSpace(members.find(m => (m.space_id || m.id) === id || m.membership_id === id));
    setShowDeleteModal(true);
  }

  async function handleDeleteConfirm() {
    setActionLoading(true);
    try {
      await dispatch(actions.addData('delete_space', {
        id: selectedSpace.space_id || selectedSpace.id,
        organization_id: org?.organization_id,
      }));
      fetchSpaces();
      setShowDeleteModal(false);
    } catch (e) { console.error(e); }
    finally { setActionLoading(false); }
  }

  const spacesLabel = getLabel('spaces') || 'Spaces';

  return (
    <>
      <div className="spaces-page">

        {/* ── Header ── */}
        <div className="sp-header">
          <div className="sp-header__left">
            <h1>{spacesLabel}</h1>
            <p>{org?.name} · Manage sub-sections, zones, and departments</p>
          </div>
          <button className="sp-btn sp-btn--primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={13} />Create {spacesLabel}
          </button>
        </div>

        {/* ── Filters ── */}
        <div className="sp-card">
          <div className="sp-card__body" style={{ padding: '11px 16px' }}>
            <div className="sp-filters">
              <div className="sp-search">
                <Search size={14} />
                <input
                  placeholder={`Search ${spacesLabel.toLowerCase()}…`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <button className="sp-apply-btn" onClick={handleSearch}>
                <Filter size={13} />Apply
              </button>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="sp-card">
          <div className="sp-card__header">
            <div className="sp-card__title"><LayoutGrid size={14} />{spacesLabel}</div>
            <span className="sp-card__meta">{membersMeta?.totalResults || 0} total {spacesLabel.toLowerCase()}</span>
          </div>
          <div className="sp-table-wrap">
            <MembersTable
              members={members || []}
              loading={loading}
              onViewDetails={handleViewDetails}
              onEdit={handleEdit}
              onDelete={handleDelete}
              currentPage={currentPage}
              perPage={perPage}
              totalCount={membersMeta?.totalResults || 0}
              onPageChange={(p) => { setCurrentPage(p); fetchSpaces(); }}
            />
          </div>
        </div>

      </div>

      {/* ── Create Modal ── */}
      <InviteMembersModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        organizationId={org?.organization_id}
        onSuccess={() => { setCurrentPage(1); fetchSpaces(); }}
      />

      {/* ── Edit Modal ── */}
      <EditSpaceModal
        show={showEditModal}
        onHide={() => { setShowEditModal(false); setSelectedSpace(null); }}
        space={selectedSpace}
        organizationId={org?.organization_id}
        onSuccess={() => { setCurrentPage(1); fetchSpaces(); }}
      />

      {/* ── Delete Confirm ── */}
      <ConfirmActionModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${spacesLabel}`}
        message={
          <div>
            <p>Delete <strong>{selectedSpace?.name}</strong>?</p>
            <p className="mt-2 text-danger"><strong>This action cannot be undone.</strong></p>
          </div>
        }
        confirmText={`Delete ${spacesLabel}`}
        type="danger"
        loading={actionLoading}
      />
    </>
  );
}